import { useTranslation } from 'react-i18next'

import {
  Button,
  CreditLetterIcon,
  EditIcon,
  InputNumber,
  Label,
  PinIcon,
  ProfileIcon,
  Select,
  Text,
} from '@mercai/clever'

import { GeneralInformationProps } from './general-information.type'

import { FinanceProfilePaymentFormsEnum } from '@/types'
import { useMemo } from 'react'
import { SectionHeader } from '../section-header'
import styles from './general-information.module.css'

export const GeneralInformation = ({
  position,
  isComplete,
  isEditable,
  quotationValues,
  setQuotationValues,
  buyerProfile,
  address,
  onHandleAddressDrawerOpen,
  onHandleBuyerDrawerOpen,
  getMetrics,
  errors,
  quotationRequestStatus,
  deliveryOption,
  isRfq = false,
  onOpenRfqEdit,
  paymentFormOption,
  paymentMethodOption,
}: GeneralInformationProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenBusinessV2')

  const onChangeValues = (key: string, value: string | number | boolean) => {
    setQuotationValues({
      ...quotationValues,
      [key]: value,
    })
  }

  const handleChangePaymentRates = (value: string) => {
    const availableRates = buyerProfile?.financeProfile?.creditInterestRates
    const rate = availableRates?.[value] || ''

    setQuotationValues((prev) => ({
      ...prev,
      creditTerm: value,
      financingCost: rate,
    }))
  }

  const handleChangePaymentForm = (
    value: keyof typeof FinanceProfilePaymentFormsEnum,
  ) => {
    if (FinanceProfilePaymentFormsEnum.credit === value) {
      setQuotationValues((prev) => ({
        ...prev,
        paymentFormOption: value,
        purchaseOnCredit: true,
        paymentMethodOption: undefined,
      }))
    } else {
      setQuotationValues((prev) => ({
        ...prev,
        paymentFormOption: value,
        purchaseOnCredit: false,
        creditTerm: null,
        financingCost: null,
      }))
    }

    getMetrics()
  }

  const ratesOptions = useMemo(() => {
    if (buyerProfile?.financeProfile?.creditInterestRates) {
      const days = Object.keys(
        buyerProfile?.financeProfile?.creditInterestRates,
      )

      return days.map((day) => ({
        label: `${day} ${t('paymentRatesDaysLabel')}`,
        value: day,
      }))
    }
    return []
  }, [buyerProfile])

  const ratePercentageValue = Number(quotationValues?.financingCost || 0) * 100

  const isProcessed = quotationRequestStatus === 'processed'

  return (
    <SectionHeader
      title={`${position}. ${t('sectionGeneralInformationTitle')}`}
      isComplete={isEditable ? isComplete : undefined}
    >
      <div>
        <div className={styles['info-block']}>
          <div className={styles.title}>
            <ProfileIcon className={styles.icon} />
            <Text variant="title-medium">{t('buyerLabel')}</Text>
          </div>
          <div className={styles['info-row']}>
            {buyerProfile && (
              <div className={styles['info-row-content']}>
                <Text className={styles['info-item']}>
                  <strong>{t('companyLabel')}:</strong>{' '}
                  {buyerProfile?.companyName || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('emailLabel')}:</strong>{' '}
                  {buyerProfile?.email || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('customerLevelLabel')}:</strong>{' '}
                  {buyerProfile?.customerLevel?.slug
                    ? tEnums(
                      `profile_customer_${buyerProfile?.customerLevel?.slug.toLocaleLowerCase()}`,
                    )
                    : '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('phoneLabel')}:</strong>{' '}
                  {buyerProfile?.phone || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('originLabel')}:</strong>{' '}
                  {buyerProfile?.type
                    ? tEnums(`profile_type_${buyerProfile?.type}`)
                    : '-'}
                </Text>
              </div>
            )}

            {!buyerProfile && (
              <Text className={styles['info-row-content']}>
                {t('buyerInformationLabel')}
              </Text>
            )}

            {!isProcessed && (
              <>
                {buyerProfile ? (
                  <Button
                    variant="secondary"
                    model="square"
                    size="small"
                    onClick={onHandleBuyerDrawerOpen}
                  >
                    <EditIcon />
                  </Button>
                ) : (
                  <Button variant="light" onClick={onHandleBuyerDrawerOpen}>
                    {t('buyerAddLabel')}
                  </Button>
                )}
              </>
            )}
          </div>
          {!!errors?.buyerProfileUuid && (
            <Text variant="text-base" className={styles.error}>
              {errors.buyerProfileUuid as string}
            </Text>
          )}
        </div>

        <div className={styles['info-block']}>
          <div className={styles.title}>
            <PinIcon className={styles.icon} />
            <Text variant="title-medium">{t('deliveryLabel')}</Text>
          </div>
          <div className={styles['info-row']}>
            {isRfq && deliveryOption === 'pick_up' && (
              <Text className={styles['info-row-content']}>
                {t('deliveryPickUpInformationLabel')}
              </Text>
            )}

            {address?.postalCode && (
              <div className={styles['info-row-content']}>
                <Text className={styles['info-item']}>
                  <strong>{t('postalCodeLabel')}:</strong>{' '}
                  {address?.postalCode || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('countryLabel')}:</strong>{' '}
                  {address?.country || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('stateLabel')}:</strong> {address?.state || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('cityLabel')}:</strong> {address?.city || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('neighborhoodLabel')}:</strong>{' '}
                  {address?.neighborhood || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('streetLabel')}:</strong> {address?.street || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('referencePointLabel')}:</strong>{' '}
                  {address?.reference || '-'}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('needsUnloadingSupportLabel')}:</strong>{' '}
                  {address?.needsUnloadingSupport
                    ? t('yesLabel')
                    : t('noLabel')}
                </Text>
                <Text className={styles['info-item']}>
                  <strong>{t('restrictedVehiclesLabel')}:</strong>{' '}
                  {address?.restrictedVehicles &&
                    address?.restrictedVehicles?.length > 0
                    ? address?.restrictedVehicles?.map((vehicle, i) => {
                      return `${i > 0 ? ',' : ''} ${tEnums(
                        `logistic_shipment_type_${vehicle}`,
                      )}`
                    })
                    : '-'}
                </Text>
              </div>
            )}

            {!address?.postalCode && deliveryOption !== 'pick_up' && (
              <Text className={styles['info-row-content']}>
                {t('deliveryInformationLabel')}
              </Text>
            )}

            {!isProcessed && (
              <>
                {address ? (
                  <Button
                    variant="secondary"
                    model="square"
                    size="small"
                    onClick={onHandleAddressDrawerOpen}
                  >
                    <EditIcon />
                  </Button>
                ) : (
                  <Button variant="light" onClick={onHandleAddressDrawerOpen}>
                    {t('deliveryAddLabel')}
                  </Button>
                )}
              </>
            )}
          </div>
          {!!errors?.deliveryAddressUuid && (
            <Text variant="text-base" className={styles.error}>
              {errors.deliveryAddressUuid as string}
            </Text>
          )}
        </div>

        {!isEditable && (
          <div className={styles['info-block']}>
            <div className={styles.title}>
              <CreditLetterIcon className={styles.icon} />
              <Text variant="title-medium">
                {t('additionalInformationLabel')}
              </Text>
            </div>
            <div className={styles['info-row']}>
              <div className={styles['info-row-content']}>
                <Text className={styles['info-item']}>
                  <strong> {t('paymentConditionLabel')}:</strong>{' '}
                  {paymentFormOption
                    ? tEnums(
                      `financial_profile_payment_forms_${paymentFormOption}`,
                    )
                    : t('notInformated')}
                </Text>

                <Text className={styles['info-item']}>
                  <strong> {t('paymentMethodLabel')}:</strong>{' '}
                  {paymentMethodOption
                    ? tEnums(
                      `financial_profile_transaction_payment_methods_${paymentMethodOption}`,
                    )
                    : t('notInformated')}
                </Text>
              </div>

              {!isProcessed && (
                <Button
                  variant="secondary"
                  model="square"
                  size="small"
                  onClick={onOpenRfqEdit}
                >
                  <EditIcon />
                </Button>
              )}
            </div>
          </div>
        )}

        {isEditable && (
          <div className={styles['info-block']}>
            <div className={styles.title}>
              <CreditLetterIcon className={styles.icon} />
              <Text variant="title-medium">{t('paymentLabel')}</Text>
            </div>
            <div className={styles['info-row-content']}>
              <div>
                <Label className={styles.label}>
                  {t('paymentConditionLabel')}
                  <span>*</span>
                </Label>
                <Select
                  value={quotationValues?.paymentFormOption}
                  onChange={(value: string) =>
                    handleChangePaymentForm(
                      value as FinanceProfilePaymentFormsEnum,
                    )
                  }
                  options={
                    buyerProfile?.financeProfile?.enabledPaymentForms?.map(
                      (form) => ({
                        label: tEnums(
                          `financial_profile_payment_forms_${form}`,
                        ),
                        value: form,
                      }),
                    ) || []
                  }
                  placeholder={t('selectPlaceholder')}
                  error={errors?.paymentFormOption as string}
                />
              </div>

              {FinanceProfilePaymentFormsEnum.credit ===
                quotationValues?.paymentFormOption && (
                  <>
                    <div>
                      <Label className={styles.label} required>
                        {t('paymentRatesLabel')}
                      </Label>
                      <Select
                        className={styles.select}
                        value={quotationValues?.creditTerm || ''}
                        onChange={handleChangePaymentRates}
                        options={ratesOptions}
                        placeholder={t('selectPlaceholder')}
                        error={errors?.creditTerm as string}
                      />
                    </div>

                    <div>
                      <Label className={styles.label} required>
                        {t('paymentFinancingCostLabel')}
                      </Label>
                      <InputNumber
                        decimalPrecision={2}
                        suffix="%"
                        value={JSON.stringify(ratePercentageValue)}
                        stepSize={1}
                        min={0}
                        onChange={(value) => {
                          const parseToDecimal = Number(value) / 100
                          onChangeValues(
                            'financingCost',
                            JSON.stringify(parseToDecimal),
                          )
                        }}
                        error={errors?.financingCost as string}
                        disabled
                      />
                    </div>
                  </>
                )}
            </div>
          </div>
        )}
      </div>
    </SectionHeader>
  )
}
